import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {
    AuditAction,
    AuditComponent,
    enumMapper,
    EnumSortDirection,
    EnumValueType,
    FeatureFlag,
    HostAuditAction,
    HostAuditComponent,
    IAuditLog,
} from '@models';
import {
    GRID_SERVICE,
    ODataGridComponent,
    STATE_HANDLER,
    urlStateHandler,
} from '@pages/generics/grid';
import {
    AuditService,
    buildODataQuery,
    FileDownloadService,
    ModalService,
    ODataQueryOptions,
    UserService,
} from '@services';
import { IDropdownOption } from '@uipath/angular/components/ui-grid';

import {
    BehaviorSubject,
    Observable,
} from 'rxjs';
import {
    finalize,
    map,
    take,
    takeUntil,
    tap,
} from 'rxjs/operators';

import { AuditLogDataModalComponent } from './modals';

// TODO: add a way to pass "OR" in filterValueMap in enumMapper()
export class GridAuditService extends AuditService {
    // map Component: `Folders` to `Folders OR Units`
    public get(queryOptions?: ODataQueryOptions, endpoint?: string) {
        if (queryOptions &&
            queryOptions.filter &&
            (queryOptions.filter as any).and
        ) {
            const component = (queryOptions.filter as any).and.find(
                (o: any) => {
                    const key = Object.keys(o)[0];
                    return key === 'Component' && o[key].eq === 'Folders';
                });

            if (component != null) {
                delete component.Component;
                component['or'] = [
                    { 'Component': { eq: 'Folders' } },
                    { 'Component': { eq: 'Units' } },
                ];
            }
        }

        return super.get(queryOptions, endpoint);
    }
}

@Component({
    templateUrl: './audit.page.html',
    providers: [{
        provide: GRID_SERVICE,
        useClass: GridAuditService,
    },
    {
        provide: STATE_HANDLER,
        useValue: urlStateHandler,
    }],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditPageComponent extends ODataGridComponent<IAuditLog, number> implements OnInit, OnDestroy {
    public components!: IDropdownOption[];
    public actions!: IDropdownOption[];

    public reportsUrl$!: Observable<string>;
    public isUsersViewGranted$ = this._appState.user.permissions.isGranted('Users.View');
    public loadingDetails$ = new BehaviorSubject(false);

    public userSuggest = this._injector.get<UserService>(UserService).suggest;
    public isHost = this._appState.user.loggedInAsHost;

    private _auditService = this._service as AuditService;
    private _modalService = this._injector.get<ModalService>(ModalService);
    private _downloadService = this._injector.get<FileDownloadService>(FileDownloadService);

    ngOnInit() {

        this.components = enumMapper(
            this._appState.user.loggedInAsHost ?
                HostAuditComponent :
                AuditComponent, {
                resourceKey: 'AuditComponent',
                type: EnumValueType.Key,
                sort: EnumSortDirection.ascending,
                filter: (key) => this._filterAuditComponent(key),
            },
        );

        this.actions = enumMapper(
            this._appState.user.loggedInAsHost ?
                HostAuditAction :
                AuditAction, {
                resourceKey: 'AuditAction',
                type: EnumValueType.Key,
                sort: EnumSortDirection.ascending,
                filter: (key) => this._filterAuditAction(key),
            },
        );

        this.reportsUrl$ = this._activeQuery$.pipe(
            map((qo) => `${this._auditService.reportsUrl}${buildODataQuery(qo)}`),
        );

        this._appState.language.change$
            .pipe(
                tap(() => this._refresh$.next()),
                takeUntil(this._destroyed$),
            )
            .subscribe();
    }

    public downloadReport(event: MouseEvent) {
        event.preventDefault();
        this.reportsUrl$.pipe(
            take(1),
        ).subscribe((url) => {
            this._downloadService.download(url);
        });
    }

    public showDetails(log: IAuditLog) {
        this.loadingDetails$.next(true);

        this._auditService
            .getLogDetails(log)
            .pipe(
                finalize(() => this.loadingDetails$.next(false)),
                takeUntil(this._destroyed$),
            )
            .subscribe((response) => {
                const isDiffView = response.changes
                    .some(change => change.isUpdate);

                this._modalService.openModal(
                    AuditLogDataModalComponent, {
                        ...response,
                        log,
                    }, {
                        size: isDiffView ?
                            'lg' :
                            undefined,
                    } ,
                );
            });
    }

    private _filterAuditComponent(key: string) {
        if (!isNaN(Number.parseInt(key, 10))) {
            return false;
        }
        switch (key) {
            case 'Tasks':
            case 'TaskCatalogs':
                return this._appState.settings.isFeatureEnabled(FeatureFlag.Tasks);
            case 'Maintenance':
                return this._appState.settings.isFeatureEnabled(FeatureFlag.Maintenance);
            default:
                return true;
        }
    }

    private _filterAuditAction(key: string) {
        if (!isNaN(Number.parseInt(key, 10))) {
            return false;
        }
        switch (key) {
            case 'Assign':
            case 'Unassign':
                return this._appState.settings.isFeatureEnabled(FeatureFlag.Tasks);
            case 'Start':
            case 'End':
            case 'Skip':
                return this._appState.settings.isFeatureEnabled(FeatureFlag.Maintenance);
            default:
                return true;
        }
    }
}
